import { useCallback, useEffect } from 'react';
import { queryOptions, useQueries, useQuery, useQueryClient, QueryClient } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';
import {
  getCredentials,
  getEmployees,
  getExternalMappings,
  getHospitals,
  getOperatingRooms,
  listUsers,
  searchAllocationsByWindow,
  searchOperatingRoomSchedules,
  searchShifts,
} from '../services/api.ts';
import type { Employee, ExternalMapping, Hospital, OperatingRoom, OperatingRoomSchedule, TenantUser } from '../services/api.ts';
import { localDateToUtcEnd, localDateToUtcStart } from '../utils/timezone.ts';
import { DAY_DATA_STALE_MS, REFERENCE_DATA_STALE_MS } from './queryClient.ts';
import { queryKeys } from './queryKeys.ts';

/**
 * Cached readers. Each factory wraps one `services/api.ts` reader, unwraps its response envelope
 * (`{ employees }`, `{ shifts }`, …) so callers get plain arrays, and pins its cache key and freshness.
 *
 * Date-keyed readers deliberately do NOT use `placeholderData: keepPreviousData`: while a date that has
 * never been fetched loads, the screen shows a loading state rather than the previous date's staff and
 * cases under the new date's header.
 */

// Rows as the board-family views consume them. The API returns richer, loosely-typed objects than the
// interfaces in api.ts describe (allocations carry colorOverrides, shifts carry shiftId, …), so the
// per-day readers stay `any` rather than pretend to a precision api.ts does not have.
/* eslint-disable @typescript-eslint/no-explicit-any */
export type ShiftRow = any;
export type AllocationRow = any;
/* eslint-enable @typescript-eslint/no-explicit-any */

// ── Reference data ─────────────────────────────────────────────────────────────────────────────────
export const employeesQuery = () =>
  queryOptions({
    queryKey: queryKeys.employees(),
    queryFn: async (): Promise<Employee[]> => (await getEmployees()).employees || [],
    staleTime: REFERENCE_DATA_STALE_MS,
  });

export const hospitalsQuery = () =>
  queryOptions({
    queryKey: queryKeys.hospitals(),
    queryFn: async (): Promise<Hospital[]> => (await getHospitals()).hospitals || [],
    staleTime: REFERENCE_DATA_STALE_MS,
  });

export const operatingRoomsQuery = () =>
  queryOptions({
    queryKey: queryKeys.operatingRooms(),
    queryFn: async (): Promise<OperatingRoom[]> => (await getOperatingRooms()).operatingRooms || [],
    staleTime: REFERENCE_DATA_STALE_MS,
  });

export const credentialsQuery = () =>
  queryOptions({
    queryKey: queryKeys.credentials(),
    queryFn: async (): Promise<string[]> => (await getCredentials()).credentials || [],
    staleTime: REFERENCE_DATA_STALE_MS,
  });

export const usersQuery = () =>
  queryOptions({
    queryKey: queryKeys.users(),
    queryFn: async (): Promise<TenantUser[]> => (await listUsers()).users || [],
    staleTime: REFERENCE_DATA_STALE_MS,
  });

export const externalMappingsQuery = (mappingType?: string) =>
  queryOptions({
    queryKey: queryKeys.externalMappings(mappingType),
    queryFn: async (): Promise<ExternalMapping[]> => (await getExternalMappings(mappingType)).mappings || [],
    staleTime: REFERENCE_DATA_STALE_MS,
  });

// ── Per-day operational data ───────────────────────────────────────────────────────────────────────
export const shiftsForDayQuery = (date: string) =>
  queryOptions({
    queryKey: queryKeys.shiftsForDay(date),
    queryFn: async (): Promise<ShiftRow[]> =>
      (await searchShifts(localDateToUtcStart(date), localDateToUtcEnd(date))).shifts || [],
    staleTime: DAY_DATA_STALE_MS,
  });

/** Shifts over an arbitrary UTC window (the roster grid's week / month view). */
export const shiftsInRangeQuery = (startUtc: string, endUtc: string) =>
  queryOptions({
    queryKey: queryKeys.shiftsInRange(startUtc, endUtc),
    queryFn: async (): Promise<ShiftRow[]> => (await searchShifts(startUtc, endUtc)).shifts || [],
    staleTime: DAY_DATA_STALE_MS,
  });

export const schedulesForDayQuery = (date: string) =>
  queryOptions({
    queryKey: queryKeys.schedulesForDay(date),
    queryFn: async (): Promise<OperatingRoomSchedule[]> =>
      (await searchOperatingRoomSchedules(localDateToUtcStart(date), localDateToUtcEnd(date))).schedules || [],
    staleTime: DAY_DATA_STALE_MS,
  });

export const allocationsForDayQuery = (date: string) =>
  queryOptions({
    queryKey: queryKeys.allocationsForDay(date),
    queryFn: async (): Promise<AllocationRow[]> =>
      (await searchAllocationsByWindow(localDateToUtcStart(date), localDateToUtcEnd(date))).allocations || [],
    staleTime: DAY_DATA_STALE_MS,
  });

// ── Hooks ──────────────────────────────────────────────────────────────────────────────────────────
export const useEmployees = () => useQuery(employeesQuery());
export const useHospitals = () => useQuery(hospitalsQuery());
export const useOperatingRooms = () => useQuery(operatingRoomsQuery());
export const useCredentials = () => useQuery(credentialsQuery());
export const useUsers = () => useQuery(usersQuery());
export const useExternalMappings = (mappingType?: string) => useQuery(externalMappingsQuery(mappingType));
export const useShiftsForDay = (date: string) => useQuery({ ...shiftsForDayQuery(date), enabled: !!date });
export const useShiftsInRange = (startUtc: string, endUtc: string) =>
  useQuery({ ...shiftsInRangeQuery(startUtc, endUtc), enabled: !!startUtc && !!endUtc });
export const useSchedulesForDay = (date: string) => useQuery({ ...schedulesForDayQuery(date), enabled: !!date });
export const useAllocationsForDay = (date: string) => useQuery({ ...allocationsForDayQuery(date), enabled: !!date });

type Updater<T> = T | ((prev: T) => T);
const EMPTY: readonly never[] = Object.freeze([]);

/**
 * Write rows straight into a cached entry, the way a `useState` setter would.
 *
 * Any fetch in flight for that entry is cancelled FIRST, so a response that left the server before this
 * write cannot land afterwards and overwrite it. (Cancellation reverts synchronously, so the write below
 * always wins.) This is what lets screens that edit optimistically — the board, Manpower — keep the cache
 * as their single source of truth.
 */
export const writeCachedRows = <T>(client: QueryClient, queryKey: readonly unknown[], next: Updater<T[]>): void => {
  void client.cancelQueries({ queryKey, exact: true });
  client.setQueryData<T[]>(queryKey, prev => {
    const base = prev ?? (EMPTY as unknown as T[]);
    return typeof next === 'function' ? (next as (p: T[]) => T[])(base) : next;
  });
};

/**
 * `useState`, backed by a cached entry: `[rows, setRows, query]`. For screens that edit a list in place
 * (rename a site, toggle a user) — the edit lands in the shared cache, so every other screen holding the
 * same list sees it too. `setRows` has `useState` setter semantics (value or updater).
 */
export const useCachedRows = <T,>(options: UseQueryOptions<T[], Error, T[], any>) => {
  const client = useQueryClient();
  const query = useQuery<T[], Error, T[], any>(options);
  const keyHash = JSON.stringify(options.queryKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setRows = useCallback((next: Updater<T[]>) => writeCachedRows<T>(client, options.queryKey as readonly unknown[], next), [client, keyHash]);
  return [(query.data ?? (EMPTY as unknown as T[])), setRows, query] as const;
};

/** How many days either side of the working day to warm in the background once it has loaded. 0 = off. */
export const ADJACENT_DAY_PREFETCH = 1;
/** Delay before warming adjacent days, so the visible day's requests are never competing with them. */
const ADJACENT_PREFETCH_DELAY_MS = 1500;

export const shiftLocalDate = (date: string, days: number): string => {
  const d = new Date(`${date}T12:00:00`); // midday, so a DST change can never roll the date
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

export const prefetchDay = (client: QueryClient, date: string): Promise<unknown> =>
  Promise.all([
    client.prefetchQuery(shiftsForDayQuery(date)),
    client.prefetchQuery(schedulesForDayQuery(date)),
    client.prefetchQuery(allocationsForDayQuery(date)),
  ]);

/**
 * Everything one operational day needs — the six reads the Board and both Manpower views share — from
 * the cache. Switching between those screens, or back to a day already viewed, renders without waiting.
 *
 * `coreLoading` is true only when the reference data or the day's shifts/cases have NEVER been fetched
 * for this date; a cached day that is merely being revalidated reports `refreshing` instead.
 * Allocations are reported separately because the screens treat an allocation failure differently from
 * a core failure (the board warns; the Manpower views block writes).
 */
export const useDayBundle = (date: string) => {
  const client = useQueryClient();
  const [employees, hospitals, operatingRooms, shifts, schedules, allocations] = useQueries({
    queries: [
      employeesQuery(),
      hospitalsQuery(),
      operatingRoomsQuery(),
      { ...shiftsForDayQuery(date), enabled: !!date },
      { ...schedulesForDayQuery(date), enabled: !!date },
      { ...allocationsForDayQuery(date), enabled: !!date },
    ],
  });
  const core = [employees, hospitals, operatingRooms, shifts, schedules];
  const coreLoading = core.some(q => q.isPending);
  const coreError = core.find(q => q.isError && q.data === undefined)?.error ?? null;
  const refreshing = [...core, allocations].some(q => q.isFetching) && !coreLoading;
  // A background revalidation failed while the last good copy is still on screen. Screens say so, so
  // nobody mistakes possibly-stale rows for live ones.
  const refreshFailed = [...core, allocations].some(q => q.isError && q.data !== undefined) && !refreshing;

  const setAllocations = useCallback(
    (next: Updater<AllocationRow[]>) => writeCachedRows(client, queryKeys.allocationsForDay(date), next),
    [client, date]
  );
  const setShifts = useCallback(
    (next: Updater<ShiftRow[]>) => writeCachedRows(client, queryKeys.shiftsForDay(date), next),
    [client, date]
  );
  /** Revalidate this day's operational data (shifts, cases, allocations) now. */
  const refetchDay = useCallback(
    () =>
      Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.shiftsForDay(date) }),
        client.invalidateQueries({ queryKey: queryKeys.schedulesForDay(date) }),
        client.invalidateQueries({ queryKey: queryKeys.allocationsForDay(date) }),
      ]),
    [client, date]
  );

  /** Re-fetch everything this day shows, reference data included (the Retry after a failed refresh). */
  const refetchAll = () => Promise.all([employees, hospitals, operatingRooms, shifts, schedules, allocations].map(q => q.refetch()));

  // Warm the neighbouring days once this one is on screen, so stepping a day either way is instant.
  useEffect(() => {
    if (!date || coreLoading || ADJACENT_DAY_PREFETCH <= 0) return;
    const timer = setTimeout(() => {
      for (let offset = 1; offset <= ADJACENT_DAY_PREFETCH; offset++) {
        void prefetchDay(client, shiftLocalDate(date, -offset));
        void prefetchDay(client, shiftLocalDate(date, offset));
      }
    }, ADJACENT_PREFETCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [client, date, coreLoading]);

  return {
    employees: (employees.data ?? EMPTY) as Employee[],
    hospitals: (hospitals.data ?? EMPTY) as Hospital[],
    operatingRooms: (operatingRooms.data ?? EMPTY) as OperatingRoom[],
    shifts: (shifts.data ?? EMPTY) as ShiftRow[],
    schedules: (schedules.data ?? EMPTY) as OperatingRoomSchedule[],
    allocations: (allocations.data ?? EMPTY) as AllocationRow[],
    coreLoading,
    coreError,
    refreshing,
    refreshFailed,
    /** The day's allocations are known (fetched, or written locally). */
    allocationsLoaded: allocations.data !== undefined,
    allocationsPending: allocations.isPending,
    allocationsError: allocations.isError && allocations.data === undefined ? allocations.error : null,
    setAllocations,
    setShifts,
    refetchDay,
    refetchAll,
  };
};
